import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, Column, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavLink } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, BidOutlook, EmployeeTimeOff, FeatureType, Grid, ModalBackDrop, ModalSize, VpUser } from '../../shared'
import { IsoDate, isoDateStringForApi, todayIso } from '../../shared/data-models/foreman-schedule'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { PrequalificationsComponent } from '../prequalification/prequalifications.component'
import { ProposalBidsComponent } from '../proposal-bids/proposal-bids.component'
import { DaysCalcComponent } from './days-calc.component'
import { OutlookComponent } from './outlook.component'
import { PreBidCompetitorsComponent } from './pre-bid-competitors.component'

@Component({
    templateUrl: './outlooks.component.html',
    styleUrls: ['./outlooks.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        FaIconComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        NgbNav,
        NgbNavItem,
        NgbNavLink,
        PrequalificationsComponent,
        ProposalBidsComponent,
    ],
})
export class OutlooksComponent extends BaseComponent implements OnDestroy {
    faCheck = faCheck
    faSpinner = faSpinner
    active = 1

    // Once a tab has been visited, its embedded component is kept alive (hidden) instead of
    // destroyed, so returning to the tab reuses the already-loaded data rather than reloading.
    prequalificationVisited = false
    proposalBidsVisited = false
    grid: Grid<BidOutlook>
    //allItems: BidOutlook[] = []
    showAll = false
    jobTypeMap!: DataMap
    countiesMap!: DataMap
    rangeMap!: DataMap
    ownersMap!: DataMap
    competitorsMap!: DataMap
    allCompetitors: any[] = []
    allUserMap!: DataMap
    activeEstimatorUserMap!: DataMap
    reviewerUserMap!: DataMap
    // Vista full names. Customer spellings differ for Jarod Moffitt, Douglas Riley, Ken Van Sickle (Buck), and Douglas Griffiths.
    private readonly reviewerNames = [
        'Jarod Moffitt',
        'Douglas Riley',
        'Chad Hunsaker',
        'Ken Van Sickle',
        'David Lancaster',
        'Douglas Griffiths',
        'Paul Hunsaker',
    ]
    deliveryTypeMap!: DataMap
    riskMap!: DataMap
    loadingFadeGain = false
    estimatorTimeOffEntries: EmployeeTimeOff[] = []
    estimatorTimeOffEndDate: IsoDate | null = null
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsEdit])
    }

    get canViewOutlooks() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsView])
    }

    get canViewPrequalification() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsView])
    }

    get canViewProposalBids() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsView])
    }

    private get baseItems(): BidOutlook[] {
        return this.showAll
            ? this.services.data.bidOutlooks.items
            : this.services.data.bidOutlooks.items.filter(x => !x.complete)
    }

    get outlookCount(): number {
        return this.baseItems.filter(x => x.bidDate).length
    }

    get opportunityCount(): number {
        return this.baseItems.filter(x => !x.bidDate).length
    }

    // The Outlooks tab (active === 1) hides the "Anticipated Bid Date" column.
    // The Opportunities tab (active === 2) hides the columns that are only relevant once a bid is scheduled.
    get isOutlooksTab(): boolean {
        return this.active === 1
    }

    get isOpportunitiesTab(): boolean {
        return this.active === 2
    }

    private _activatedRoute = inject(ActivatedRoute)

    constructor() {
        super()

        // Initialize delivery type map with static options
        const deliveryTypeOptions = [
            { id: 'Bonfire', name: 'Bonfire' },
            { id: 'U3P', name: 'U3P' },
            { id: 'Projectmates', name: 'Projectmates' },
            { id: 'Email', name: 'Email' },
            { id: 'Hand deliver', name: 'Hand deliver' },
            { id: 'UDOT', name: 'UDOT' },
            { id: 'Sandy Ionwave', name: 'Sandy Ionwave' },
            { id: 'Virtual Bid', name: 'Virtual Bid' },
        ]
        this.deliveryTypeMap = new DataMap(deliveryTypeOptions, 'id', 'name')
        this.riskMap = new DataMap(
            [1, 2, 3, 4, 5].map(level => ({ id: level, name: String(level) })),
            'id',
            'name',
        )

        this.grid = new Grid<BidOutlook>(this.services, 'id', this)
            .wrapColumnHeaders()
            .filterBy(item => [item.bidDescription])
            .setupModalEditing(
                this.services.data.bidOutlooks,
                'Bid Outlook',
                OutlookComponent,
                () => new BidOutlook(),
                this,
                ModalSize.medium,
            )
            .isReadOnly(!this.canEdit)
            .editOnDoubleClick(false)
        this.grid.itemAdded.on(this.filterItems.bind(this))
        this.grid.itemEdited.on(this.filterItems.bind(this))

        this.refresh()
    }

    ngOnDestroy() {
        this.grid.itemAdded.off(this.filterItems)
        this.grid.itemEdited.off(this.filterItems)
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.bidOutlooks.queryAll(),
                    this.services.data.jobTypes.queryAll(),
                    this.services.data.counties.queryAll(),
                    this.services.data.bidRanges.queryAll(),
                    this.services.data.get('/api/VpOwner/GetOwners'),
                    this.services.data.get('/api/VpOwner/GetCompetitors'),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.get('api/VpUser/GetVistaEstimatorUsers'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.calculateNewWeeks()

                        const outlookId = this.services.angular.getQueryParam('id', this._activatedRoute)
                        const readyForReview = this.services.angular.getQueryParam('readyForReview', this._activatedRoute)
                        if (outlookId && readyForReview) {
                            const item = this.services.data.bidOutlooks.items.find(x => x.id.toLowerCase() === outlookId.toLowerCase())
                            if (item) {
                                item.readyForReview = readyForReview === 'true'
                                this.updateBids([item])

                                //?id=DECFB500-C619-4293-AA2F-AD3FA32DA7FD&readyForReview=true
                            }
                        }

                        this.jobTypeMap = new DataMap(result[1], 'id', 'fullName')
                        this.countiesMap = new DataMap(result[2], 'id', 'combinedName')
                        this.rangeMap = new DataMap(result[3], 'id', 'name')
                        this.ownersMap = new DataMap(result[4], 'id', 'name')
                        this.competitorsMap = new DataMap(result[5], 'id', 'name')
                        this.allCompetitors = result[5] as any[]

                        // We want the grid to show a value for any user, even inactive ones or ones who are no longer estimators.
                        const allUsers = result[6] as VpUser[]
                        this.allUserMap = new DataMap(allUsers, 'id', 'name')

                        // When the grid is edited, we will only show active users as options.
                        this.activeEstimatorUserMap = new DataMap(result[7] as VpUser[], 'id', 'name')
                        this.reviewerUserMap = new DataMap(
                            allUsers.filter(user => this.reviewerNames.includes(user.name)),
                            'id',
                            'name',
                        )

                        setTimeout(() => this.grid.flexGrid.hostElement.addEventListener('click', this.handleDayClick.bind(this)), 500)
                        this.filterItems()
                        this.loadEstimatorTimeOff()
                        this.refreshFadeGain()
                    },
                }),
        )
    }

    refreshFadeGain() {
        this.loadingFadeGain = true
        this.cancelableRequest(
            'OutlookFadeGain',
            true,
            this.services.data.load(false, true, this.services.data.get('/api/BidOutlook/GetFadeGain')).subscribe({
                next: result => {
                    const dtos = result[0] as any[]
                    // Update every loaded bid outlook (not just the currently filtered tab) so the
                    // fade/gain values are present regardless of which tab is active when the
                    // (slow) request resolves.
                    this.services.data.bidOutlooks.items.forEach(x => {
                        const match = dtos.find(y => y.id == x.id)
                        if (match) {
                            x.laborFadeGain = match.laborFadeGain
                            x.costFadeGain = match.costFadeGain
                        }
                    })
                    this.grid.refresh()
                    this.loadingFadeGain = false
                },
            }),
        )
    }

    onTabChange() {
        if (this.active === 3) this.prequalificationVisited = true
        if (this.active === 4) this.proposalBidsVisited = true
        this.filterItems()
    }

    filterItems() {
        if (this.active !== 1 && this.active !== 2) return
        this.grid.items =
            this.active === 1 ? this.baseItems.filter(x => x.bidDate) : this.baseItems.filter(x => !x.bidDate)
    }

    calculateNewWeeks() {
        let week = 0
        for (let i = 0; i < this.services.data.bidOutlooks.items.length; i++) {
            const item = this.services.data.bidOutlooks.items[i]
            item.isNewWeek = false
            if (i === 0 && item.bidDate) {
                week = item.bidDate.yearAndWeek()
                continue
            }
            if (item.bidDate) {
                if (item.bidDate.yearAndWeek() > week) {
                    week = item.bidDate.yearAndWeek()
                    item.isNewWeek = true
                }
            }
        }
    }

    handleDayClick(event) {
        const target = event.target as HTMLElement
        if (target && target.id === 'days-btn') {
            if (!this.grid.selectedItem) {
                this.services.notification.warning('Please select an item in the grid before calculating a date from days.')
            } else {
                this.openDiDetails()
            }
            console.log(event)
        }
    }

    openDiDetails() {
        const ref = this.services.modals.open(DaysCalcComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as DaysCalcComponent
        instance.target = this.grid.selectedItem
        ref.result.then(
            () => {
                this.grid.selectedItem.completionDate = instance.calcDate
                this.updateBids([this.grid.selectedItem])
                this.grid.refresh()
            },
            () => {
                //
            },
        )
    }

    mainGridItemFormatter(panel: GridPanel, r, c, cell: HTMLDivElement) {
        const row = panel.grid.rows[r]
        const col = panel.grid.columns[c]
        if (panel.cellType === CellType.ColumnHeader) {
            if (col.binding === 'completionDate') {
                cell.innerHTML += `<button id="days-btn" class="btn btn-xs btn-secondary">Days</button>`
            }
        }
        if (panel.cellType === CellType.Cell) {
            const rowItem = row.dataItem

            cell.classList.remove('dark-yellow-cell')
            cell.classList.remove('red-bottom-border')

            if (rowItem.needPlans && col.binding === 'needPlans') {
                cell.classList.add('dark-yellow-cell')
            }

            if (rowItem.mandatory && col.binding === 'mandatory') {
                cell.classList.add('dark-yellow-cell')
            }

            if (rowItem.isNewWeek) {
                cell.classList.add('red-bottom-border')
            }

            if (col.binding === 'estimatorTimeOffNames') {
                cell.title = rowItem.estimatorTimeOffTooltip ?? ''
            }
        }
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column data map to just active users, so inactive users cannot be selected.
        const col = flexGrid.columns[args.col] as Column
        if (col.binding == 'estimatorHrRef' || col.binding == 'personGoingHrRef') col.dataMap = this.activeEstimatorUserMap
        if (col.binding == 'reviewerHrRef') col.dataMap = this.reviewerUserMap
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column mapping back to all users, so inactive user names still appear.
        const col = flexGrid.columns[args.col]
        if (col.binding == 'estimatorHrRef' || col.binding == 'personGoingHrRef' || col.binding == 'reviewerHrRef') col.dataMap = this.allUserMap

        if (args.cancel) return
        this.updateBids([flexGrid.rows[args.row].dataItem])
        if (col.binding === 'bidDate') {
            this.filterItems()
            const bidDateIso = isoDateStringForApi(flexGrid.rows[args.row].dataItem.bidDate)
            if (bidDateIso && (!this.estimatorTimeOffEndDate || bidDateIso > this.estimatorTimeOffEndDate)) {
                this.loadEstimatorTimeOff()
            } else {
                this.applyEstimatorTimeOff()
            }
        }
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem
            itemIds.push(dataItem.id)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        flexGrid['controller'].itemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter(x => flexGrid['controller'].itemIds.indexOf(x.id) > -1)
        this.updateBids(itemsToSave)
        flexGrid['controller'].pastedRimnumbers = [] // clear immediately and not on promise fulfillment.
    }

    updateBids(items: BidOutlook[]) {
        this.services.data
            .put('api/BidOutlook/BulkUpdate', items)
            .subscribe({ error: () => this.services.notification.error('The bid outlook changes were not successfully saved') })
    }

    complete() {
        this.services.modals
            .promptBool(
                'Mark Complete?',
                "Are you sure you want to mark the job '" + this.grid.selectedItem.bidDescription + "' as complete?",
                'Was the job Bid?',
                '',
                ModalSize.small,
            )
            .then(result => {
                const params = new HttpParams().set('wasBid', result[0])
                this.services.data.bidOutlooks.edit(this.grid.selectedItem, params, 'MarkComplete').subscribe({
                    next: () => {
                        this.filterItems()
                    },
                })
            })
            .catch(() => {
                //
            })
    }

    openPreBidCompetitorsModal(bidOutlook: BidOutlook) {
        const ref = this.services.modals.open(PreBidCompetitorsComponent, ModalSize.large, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as PreBidCompetitorsComponent
        instance.bidOutlookId = bidOutlook.id
        instance.bidDescription = bidOutlook.bidDescription
        instance.allCompetitors = this.allCompetitors
        instance.selectedCompetitorIds = bidOutlook.competitorIds ? [...bidOutlook.competitorIds] : []

        ref.result.then(
            () => {
                // Refresh the grid to show updated data
                this.refresh()
            },
            () => {
                // User cancelled, do nothing
            },
        )
    }

    getPreBidCompetitorsCount(bidOutlook: BidOutlook): string {
        if (bidOutlook.competitorIds && bidOutlook.competitorIds.length > 0) {
            return `${bidOutlook.competitorIds.length} ${bidOutlook.competitorIds.length === 1 ? 'Competitor' : 'Competitors'}`
        }
        return 'No Competitors'
    }

    private getMaxBidDateIso(): IsoDate | null {
        const bidDates = this.services.data.bidOutlooks.items
            .filter(x => x.bidDate)
            .map(x => isoDateStringForApi(x.bidDate))
            .filter(x => x)
        if (!bidDates.length) return null
        return bidDates.sort().at(-1)!
    }

    private loadEstimatorTimeOff() {
        const startDate = todayIso()
        const maxBidDate = this.getMaxBidDateIso()
        if (!maxBidDate) {
            this.estimatorTimeOffEntries = []
            this.estimatorTimeOffEndDate = startDate
            this.applyEstimatorTimeOff()
            return
        }

        const params = new HttpParams().set('startDate', startDate).set('endDate', maxBidDate)
        this.cancelableRequest(
            'EstimatorTimeOff',
            true,
            this.services.data.get<EmployeeTimeOff[]>('api/VpUser/GetEstimatorTimeOff', params).subscribe({
                next: entries => {
                    this.estimatorTimeOffEntries = (entries ?? []).map(e => ({
                        ...e,
                        startDate: isoDateStringForApi(e.startDate as string | Date | null | undefined),
                        endDate: isoDateStringForApi(e.endDate as string | Date | null | undefined),
                    }))
                    this.estimatorTimeOffEndDate = maxBidDate
                    this.applyEstimatorTimeOff()
                },
            }),
        )
    }

    private applyEstimatorTimeOff() {
        const today = todayIso()
        for (const item of this.services.data.bidOutlooks.items) {
            if (!item.bidDate) {
                item.estimatorTimeOffNames = ''
                item.estimatorTimeOffTooltip = ''
                continue
            }

            const bidDateIso = isoDateStringForApi(item.bidDate)
            const matching = this.estimatorTimeOffEntries
                .filter(e => e.startDate && e.endDate && e.startDate <= bidDateIso && e.endDate >= today)
                .sort((a, b) => a.startDate.localeCompare(b.startDate) || a.name.localeCompare(b.name))

            const seenNames = new Set<string>()
            const names: string[] = []
            for (const entry of matching) {
                if (!seenNames.has(entry.name)) {
                    seenNames.add(entry.name)
                    names.push(entry.name)
                }
            }

            item.estimatorTimeOffNames = names.join(', ')
            item.estimatorTimeOffTooltip = matching
                .map(
                    e =>
                        `${e.name} - ${e.leaveType} (${e.status}): ${this.formatShortIsoDate(e.startDate)} - ${this.formatShortIsoDate(e.endDate)}`,
                )
                .join('\n')
        }
        this.grid.refresh()
    }

    private formatShortIsoDate(iso: string): string {
        const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
        const dt = new Date(Date.UTC(y, m - 1, d))
        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    }
}
